import { isMobileAppRuntime } from "../config/mobileRuntime";
import { getPublicWebOrigin, isNativeApp } from "../native/runtime";

const cloudPositionsMobile = {
  personal: { x: 16, y: 25 },
  utility: { x: 55, y: 45 },
  demands: { x: 2, y: 20 },
  interests: { x: 60, y: 46 },
  social: { x: 40, y: 50 },
};

const cloudPositionsDesktop = {
  personal: { x: 29, y: 35 },
  utility: { x: 42, y: 45 },
  demands: { x: 60, y: 21 },
  interests: { x: 78, y: 53 },
  social: { x: 11, y: 54 },
};

export const DEFAULT_CASTLE_SKIN_ID = "Grimwarth";

// Bump REACT_APP_CASTLE_SKINS_VERSION on each castle image replacement
// to invalidate browser/CDN cache for unchanged filenames.
export const CASTLE_SKINS_ASSET_VERSION =
  process.env.REACT_APP_CASTLE_SKINS_VERSION || "2026-04-01-1";

export const CASTLE_SKINS_REMOTE_ORIGIN = String(
  process.env.REACT_APP_CASTLE_SKINS_REMOTE_ORIGIN || "https://vaultgrid.net"
).replace(/\/+$/, "");

const normalizeSkinId = (skinId) =>
  String(skinId || DEFAULT_CASTLE_SKIN_ID)
    .trim()
    .replace(/\s+/g, "_");

const withCastleSkinVersion = (path) =>
  `${path}?v=${encodeURIComponent(CASTLE_SKINS_ASSET_VERSION)}`;

const getCastleSkinMobilePath = (skinId) =>
  `/assets/castle_skins/full/mobileFull/${normalizeSkinId(skinId)}.webp`;

export const getBundledCastleSkinFallbackUrl = () =>
  withCastleSkinVersion(getCastleSkinMobilePath(DEFAULT_CASTLE_SKIN_ID));

export const getCastleSkinMobileUrl = (skinId) => {
  const normalizedSkinId = normalizeSkinId(skinId);
  const localPath = getCastleSkinMobilePath(normalizedSkinId);

  // Mobile binaries keep only the default full-size castle. Every other
  // original full-size image is served on demand from the public asset host.
  // Web builds remain same-origin so the normal website deployment is
  // unchanged.
  if (isMobileAppRuntime() && normalizedSkinId !== DEFAULT_CASTLE_SKIN_ID) {
    const remoteOrigin = isNativeApp()
      ? getPublicWebOrigin()
      : CASTLE_SKINS_REMOTE_ORIGIN;
    return withCastleSkinVersion(`${remoteOrigin}${localPath}`);
  }

  return withCastleSkinVersion(localPath);
};

export const getCastleSkinDesktopUrl = (skinId) => {
  if (isMobileAppRuntime()) {
    return getCastleSkinMobileUrl(skinId);
  }

  return withCastleSkinVersion(
    `/assets/castle_skins/full/desktopFull/${normalizeSkinId(skinId)}.webp`
  );
};

export const getCastleSkinThumbUrl = (skinId) =>
  withCastleSkinVersion(
    `/assets/castle_skins/thumb/${normalizeSkinId(skinId)}.webp`
  );

const CASTLE_BACKGROUND_PALETTES = [
  {
    match: [
      "night",
      "noct",
      "nox",
      "dark",
      "dusk",
      "gloam",
      "eclipse",
      "midnight",
      "obsidian",
      "sable",
      "umbral",
      "ebon",
      "raven",
      "grim",
    ],
    colors: ["#4F67A9", "#1C2F69", "#070C1C"],
  },
  {
    match: ["frost", "ice", "silver", "white", "ivory", "pearl", "crystal", "mirror", "glacis"],
    colors: ["#86A8D8", "#405D8F", "#121B31"],
  },
  {
    match: ["sun", "gold", "aur", "amber", "dawn", "ember", "pyre", "cinder", "brass", "bronze"],
    colors: ["#A98D70", "#5B4C73", "#12142C"],
  },
  {
    match: ["jade", "verdant", "green", "fern", "pine", "meadow", "viridian", "blossom", "rose", "sakura"],
    colors: ["#7FA59B", "#335E71", "#0B1923"],
  },
  {
    match: ["tide", "water", "bay", "lagoon", "harbor", "marina", "seastar", "azure", "stream"],
    colors: ["#6F9FD6", "#285A91", "#071525"],
  },
  {
    match: ["dune", "stone", "cliff", "hollow", "forge", "iron", "marble", "cairn"],
    colors: ["#8793A8", "#45536F", "#111722"],
  },
  {
    match: ["cloud", "sky", "lumin", "celest", "aether", "zephyr", "opal", "glass"],
    colors: ["#83A1EF", "#3C63CE", "#0B1530"],
  },
];

const DEFAULT_CASTLE_BACKGROUND_PALETTE = ["#3E5967", "#172B47", "#071224"];

export const getCastleBackgroundPalette = (skinId) => {
  const normalizedId = String(skinId || DEFAULT_CASTLE_SKIN_ID).toLowerCase();
  const palette = CASTLE_BACKGROUND_PALETTES.find(({ match }) =>
    match.some((token) => normalizedId.includes(token))
  );
  return palette?.colors || DEFAULT_CASTLE_BACKGROUND_PALETTE;
};

const clampChannel = (value) => Math.max(0, Math.min(255, Math.round(value)));

const normalizeRgb = (color) => {
  if (!color) return null;
  if (typeof color === "string") {
    const hex = color.replace("#", "").trim();
    if (hex.length === 6) {
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
      };
    }
    return null;
  }
  const { r, g, b } = color;
  if (![r, g, b].every(Number.isFinite)) return null;
  return { r: clampChannel(r), g: clampChannel(g), b: clampChannel(b) };
};

const mixRgb = (color, target, amount) => ({
  r: clampChannel(color.r + (target.r - color.r) * amount),
  g: clampChannel(color.g + (target.g - color.g) * amount),
  b: clampChannel(color.b + (target.b - color.b) * amount),
});

const rgbToCss = ({ r, g, b }) => `rgb(${r} ${g} ${b})`;

export const getCastleBackgroundStyle = (skinId, sampledTopColor = null) => {
  const sampled = normalizeRgb(sampledTopColor);
  if (sampled) {
    const top = mixRgb(sampled, { r: 0, g: 0, b: 0 }, 0.08);
    const middle = mixRgb(sampled, { r: 0, g: 0, b: 0 }, 0.2);
    const bottom = mixRgb(sampled, { r: 0, g: 0, b: 0 }, 0.36);
    return {
      backgroundImage: `linear-gradient(180deg, ${rgbToCss(top)} 0%, ${rgbToCss(middle)} 48%, ${rgbToCss(bottom)} 100%)`,
      backgroundColor: rgbToCss(middle),
    };
  }

  const [top, middle, bottom] = getCastleBackgroundPalette(skinId);
  return {
    backgroundImage: `linear-gradient(180deg, ${top} 0%, ${middle} 42%, ${bottom} 100%)`,
    backgroundColor: bottom,
  };
};

const skinIds = [
  'Celestgar',
  'Crestmoor',
  'Dunkelstein',
  'Ebonspire',
  'Elarion',
  'Grimwarth',
  'Mondschloss',
  'Myrgald',
  'Nightshade_Bastion',
  'Noxkarn',
  'Stormwald',
  'Thaloria',
  'Zornvelt',
  'Aetherkeep',
  'Cinderhold',
  'Frostwarden',
  'Ironveil',
  'Ravencrest',
  'Amberfall',
  'Brighthaven',
  'Crowspire',
  'Duskmere',
  'Emberfort',
  'Falconreach',
  'Goldwyck',
  'Hallowcrest',
  'Ivorspire',
  'Jadebastion',
  'Kingshollow',
  'Luminhold',
  'Mistwarden',
  'Northvale',
  'Opalkeep',
  'Pyreholm',
  'Queenswatch',
  'Redcliff',
  'Sunhaven',
  'Tideguard',
  'Umbracrown',
  'Valeward',
  'Whiteharbor',
  'Xanthera',
  'Yarrowkeep',
  'Zephyrhold',
  'Auricspire',
  'Bronzehart',
  'Cloudmarch',
  'Ivorykeep',
  'Suncourt',
  'Tidecrown',
  'Blossomkeep',
  'Grandforum',
  'Frostmirror',
  'Dunegleam',
  'Moondome',
  'Whiteforge',
  'Goldenspire',
  'Dawnterrace',
  'Glasshaven',
  'Pinewatch',
  'Rosecourt',
  'Ambercrest',
  'Highpine',
  'Hearthpeak',
  'Stoneveil',
  'Frostpagoda',
  'SolsticeCrown',
  'Mirrorbay',
  'LunarisDome',
  'Sunstep',
  'ObsidianAxis',
  'Goldray',
  'Skylace',
  'Dawntier',
  'AzureMirage',
  'SunflareGate',
  'Silverstream',
  'GildedArch',
  'LotusBastion',
  'Crownwheel',
  'PearlMeridian',
  'SunforgeArena',
  'VerdantMarina',
  'CrystalLagoon',
  'EclipseHalo',
  'AurumGate',
  'SolarLotus',
  'GlacisCrown',
  'AzurePavilion',
  'SakuraPagoda',
  'ViridianRing',
  'RoselightAtrium',
  'MarbleCourt',
  'LotusHalo',
  'IvoryBloom',
  'MidnightMeridian',
  'Emberpeak',
  'NeonBastion',
  'Glassgrove',
  'AzureCascades',
  'TidefireBaths',
  'Suncoil',
  'RegalMeridian',
  'PearlOrbit',
  'AureateGate',
  'CrownBasilica',
  'SapphireForum',
  'SunvaultRotunda',
  'VerdantHalo',
  'IvoryCitadel',
  'SunreachKeep',
  'NoctisThrone',
  'SakuraSummit',
  'AureliaSteps',
  'Cliffhaven',
  'Tidepalace',
  'WhiteSanctum',
  'Fernspire',
  'EmberCairn',
  'Solharbor',
  'Nightbarrow',
  'ObsidianThrone',
  'LavenderHarbor',
  'StillwaterManor',
  'AmbervineCourt',
  'IvoryAurora',
  'DuskholdDomes',
  'RadiantHalo',
  'Kingsarch',
  'GreenwoodVale',
  'Hollowforge',
  'Ambercanal',
  'StarwhiteForum',
  'PearlCrest',
  'SableDunes',
  'VerdantCourt',
  'Dawnspire',
  'BrassRotunda',
  'SunstoneTerrace',
  'Mirrorhold',
  'SeastarPavilion',
  'Rosepagoda',
  'Gloamspire',
  'SolarArena',
  'Frostchapel',
  'Meadowcrown',
  'Cloudbastion',
  'Blushcourtyard',
];

const buildSkin = (id) => {
  const mobilePath = getCastleSkinMobileUrl(id);
  const desktopPath = getCastleSkinDesktopUrl(id);
  return {
    id,
    full: desktopPath,
    mobileFull: mobilePath,
    desktopFull: desktopPath,
    thumb: getCastleSkinThumbUrl(id),
    backgroundPalette: getCastleBackgroundPalette(id),
    cloudPositionsMobile,
    cloudPositionsDesktop,
  };
};

export const skins = skinIds.map(buildSkin);
